/**
 * VoiceSettings Component
 *
 * A settings panel for configuring voice preferences including:
 * - Language selection
 * - Voice selection
 * - Speech rate, pitch, and volume
 * - Auto-read preferences
 *
 * @example
 * <VoiceSettings />
 */

import React, { useState } from 'react';
import {
  FaVolumeUp,
  FaMicrophone,
  FaLanguage,
  FaPlay,
  FaRedo,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import { useVoice, useVoiceSupport } from './VoiceProvider';
import useSpeechSynthesis from '../../hooks/useSpeechSynthesis';

/**
 * VoiceSettings component for user preferences
 */
const VoiceSettings = () => {
  const voiceSupport = useVoiceSupport();
  const {
    settings,
    setLanguage,
    setRate,
    setPitch,
    setVolume,
    setVoiceName,
    setAutoReadMessages,
    setAutoReadAlerts,
    setShowVoiceButtons,
    resetSettings,
    voices,
    currentVoice,
    getVoicesByLanguage,
  } = useVoice();

  const { speak, cancel, isSpeaking } = useSpeechSynthesis({
    language: settings.language,
  });

  const [testPlayed, setTestPlayed] = useState(false);

  // Available languages (common ones)
  const languages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'en-AU', name: 'English (Australia)' },
    { code: 'es-ES', name: 'Spanish (Spain)' },
    { code: 'es-MX', name: 'Spanish (Mexico)' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'zh-CN', name: 'Chinese (Simplified)' },
    { code: 'hi-IN', name: 'Hindi' },
  ];

  // Get voices for selected language
  const availableVoices = getVoicesByLanguage(settings.language);

  const handleTestVoice = () => {
    if (isSpeaking) {
      cancel();
    } else {
      speak('Hello! This is a test of your voice settings. Your itineraries will sound like this when read aloud.');
      setTestPlayed(true);
    }
  };

  const handleReset = () => {
    resetSettings();
    setTestPlayed(false);
  };

  // If voice is not supported, show a message
  if (!voiceSupport.any) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
            <FaVolumeUp className="text-neutral-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-800">Voice Settings</h3>
            <p className="text-sm text-neutral-500">Text-to-speech and voice input</p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
          <FaTimesCircle className="text-amber-500 text-2xl mx-auto mb-2" />
          <p className="text-amber-800 font-medium">Voice Not Supported</p>
          <p className="text-amber-600 text-sm mt-1">
            Your browser doesn't support speech features. Try using Chrome, Edge, or Safari.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <FaVolumeUp className="text-xl text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-800">Voice Settings</h3>
              <p className="text-sm text-neutral-500">Configure text-to-speech and voice input</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {voiceSupport.speechSynthesis && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                <FaCheckCircle className="text-xs" /> TTS
              </span>
            )}
            {voiceSupport.speechRecognition && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                <FaMicrophone className="text-xs" /> STT
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Language Selection */}
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
            <FaLanguage className="text-indigo-500" />
            Language
          </label>
          <select
            value={settings.language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border-2 border-neutral-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all bg-white"
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>

        {/* Voice Selection */}
        {voiceSupport.speechSynthesis && availableVoices.length > 0 && (
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
              <FaVolumeUp className="text-indigo-500" />
              Voice
            </label>
            <select
              value={currentVoice?.name || ''}
              onChange={(e) => setVoiceName(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border-2 border-neutral-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all bg-white"
            >
              {availableVoices.map((voice) => (
                <option key={voice.name} value={voice.name}>
                  {voice.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Speech Rate */}
        <div>
          <label className="flex items-center justify-between text-sm font-semibold text-neutral-700 mb-2">
            <span>Speech Speed</span>
            <span className="text-indigo-600 font-bold">{settings.rate.toFixed(1)}x</span>
          </label>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={settings.rate}
            onChange={(e) => setRate(parseFloat(e.target.value))}
            className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          <div className="flex justify-between text-xs text-neutral-400 mt-1">
            <span>Slower</span>
            <span>Normal</span>
            <span>Faster</span>
          </div>
        </div>

        {/* Pitch */}
        <div>
          <label className="flex items-center justify-between text-sm font-semibold text-neutral-700 mb-2">
            <span>Pitch</span>
            <span className="text-indigo-600 font-bold">{settings.pitch.toFixed(1)}</span>
          </label>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={settings.pitch}
            onChange={(e) => setPitch(parseFloat(e.target.value))}
            className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          <div className="flex justify-between text-xs text-neutral-400 mt-1">
            <span>Lower</span>
            <span>Normal</span>
            <span>Higher</span>
          </div>
        </div>

        {/* Volume */}
        <div>
          <label className="flex items-center justify-between text-sm font-semibold text-neutral-700 mb-2">
            <span>Volume</span>
            <span className="text-indigo-600 font-bold">{Math.round(settings.volume * 100)}%</span>
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={settings.volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
        </div>

        {/* Auto-read Options */}
        <div className="border-t border-neutral-100 pt-6">
          <h4 className="text-sm font-semibold text-neutral-700 mb-4">Auto-Read Options</h4>

          <div className="space-y-3">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-neutral-600">Auto-read AI chat messages</span>
              <input
                type="checkbox"
                checked={settings.autoReadMessages}
                onChange={(e) => setAutoReadMessages(e.target.checked)}
                className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-neutral-600">Auto-read travel alerts</span>
              <input
                type="checkbox"
                checked={settings.autoReadAlerts}
                onChange={(e) => setAutoReadAlerts(e.target.checked)}
                className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-neutral-600">Show voice buttons in UI</span>
              <input
                type="checkbox"
                checked={settings.showVoiceButtons}
                onChange={(e) => setShowVoiceButtons(e.target.checked)}
                className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
              />
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={handleTestVoice}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
              isSpeaking
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-indigo-500 text-white hover:bg-indigo-600'
            }`}
          >
            <FaPlay className={isSpeaking ? 'animate-pulse' : ''} />
            {isSpeaking ? 'Stop' : 'Test Voice'}
          </button>

          <button
            onClick={handleReset}
            className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium bg-neutral-100 text-neutral-600 hover:bg-neutral-200 transition-all"
          >
            <FaRedo />
            Reset
          </button>
        </div>

        {testPlayed && !isSpeaking && (
          <p className="text-center text-sm text-green-600">
            <FaCheckCircle className="inline mr-1" />
            Voice test completed. Settings are saved automatically.
          </p>
        )}
      </div>
    </div>
  );
};

export default VoiceSettings;
